import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import { useDebouncedCallback } from 'use-debounce';

// Hooks
import useIsomorphicLayoutEffect from 'hooks/use-isomorphic-layout-effect';

// Utils
import bemify from 'utils/bemify';

// Components
import Button from 'components/button';
import TagButton from 'components/tag-button';

// Styles
import stylesModule from './styles.module.scss';


const styles = bemify(stylesModule);

const HorizontalMenu = ({ activeItem = null, className = null, items = null, onItemClick = () => {} }) => {
  const menu = useRef(null);
  const menuWrapper = useRef(null);

  const [hasOverflow, setHasOverflow] = useState(false);
  const [isFullyScrolledRight, setIsFullyScrolledRight] = useState(false);
  const [isScrolledRight, setIsScrolledRight] = useState(false);

  const handleMenuScroll = () => {
    let maxScrollLeft = menu.current.scrollWidth - menu.current.clientWidth;

    if (hasOverflow && isScrolledRight) maxScrollLeft -= 40;
    if (hasOverflow && !isFullyScrolledRight) maxScrollLeft -= 40;

    setIsScrolledRight(menu.current.scrollLeft > 1);
    setIsFullyScrolledRight(maxScrollLeft - menu.current.scrollLeft < 1);
  };

  const handleMenuWrapperResize = useDebouncedCallback((entries) => {
    for (const entry of entries) {
      if (entry.target === menuWrapper.current) {
        if (menu?.current) {
          setHasOverflow(menu.current.scrollWidth > menuWrapper.current.offsetWidth);

          handleMenuScroll();
        }
      }
    }
  }, 100);

  useIsomorphicLayoutEffect(() => {
    const resizeObserver = new ResizeObserver(handleMenuWrapperResize);

    if (menuWrapper.current) {
      handleMenuWrapperResize();

      resizeObserver.observe(menuWrapper.current);
    }

    if (menu.current) {
      menu.current.addEventListener('scroll', handleMenuScroll);
    }

    return () => {
      resizeObserver.disconnect();

      menu.current.removeEventListener('scroll', handleMenuScroll);
    };
  }, [handleMenuWrapperResize]);

  if (!Array.isArray(items) || items.length <= 1) return null;

  const handleItemLinkClick = (itemId) => {
    onItemClick(itemId);
  };

  const handleScrollLeftButtonClick = () => {
    menu.current.scrollBy({
      behavior: 'smooth',
      left: -100
    });
  };

  const handleScrollRightButtonClick = () => {
    menu.current.scrollBy({
      behavior: 'smooth',
      left: 100
    });
  };

  return (
    <div
      ref={menuWrapper}
      className={`${styles.horizontalMenu__outerWrapper}${className ? ` ${className}` : ''}`}
    >
      <div
        ref={menu}
        className={`${styles.horizontalMenu}${
          hasOverflow && isScrolledRight ? ` ${styles.horizontalMenu_scrolledRight}` : ''}${
          hasOverflow && isFullyScrolledRight ? ` ${styles.horizontalMenu_scrolledRightFull}` : ''}`}
      >
        {items.map((entry) => {
          const itemSlug = entry.slug;

          return (
            <Link
              key={itemSlug}
              href={`#${itemSlug}`}
              legacyBehavior
              replace
            >
              <a
                className={entry.active ? null : styles.horizontalMenu__entry_disabled}
                role="button"
                tabIndex={-1}
                onClick={() => handleItemLinkClick(itemSlug)}
                onKeyDown={() => handleItemLinkClick(itemSlug)}
              >
                <TagButton
                  className={`${styles.horizontalMenu__entry}${itemSlug === activeItem ? ` ${styles.horizontalMenu__entry_active}` : ''}`}
                  label={entry.name}
                />
              </a>
            </Link>
          );
        })}
      </div>
      {hasOverflow && isScrolledRight && (
        <div className={`${styles.horizontalMenu__scrollIconWrapper} ${styles.horizontalMenu__scrollIconWrapper_left}`}>
          <Button
            className={`${styles.horizontalMenu__scrollButton} ${styles.horizontalMenu__scrollButton_left}`}
            icon="chevron-left"
            theme="unstyled"
            onClick={handleScrollLeftButtonClick}
          />
        </div>
      )}
      {hasOverflow && !isFullyScrolledRight && (
        <div className={`${styles.horizontalMenu__scrollIconWrapper} ${styles.horizontalMenu__scrollIconWrapper_right}`}>
          <Button
            className={`${styles.horizontalMenu__scrollButton} ${styles.horizontalMenu__scrollButton_right}`}
            icon="chevron-right"
            theme="unstyled"
            onClick={handleScrollRightButtonClick}
          />
        </div>
      )}
    </div>
  );
};

HorizontalMenu.propTypes = {
  activeItem: PropTypes.string,
  className: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.shape({
      label: PropTypes.string
    }),
    PropTypes.string
  ])),
  onItemClick: PropTypes.func
};

export default HorizontalMenu;
